import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { MessagesSquare } from "lucide-react";
import { z } from "zod";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/view/components/ui/dialog";
import { Button } from "@/view/components/ui/button";
import { Input } from "@/view/components/ui/input";
import { Label } from "@/view/components/ui/label";
import { Textarea } from "@/view/components/ui/textarea";
import { Spinner } from "@/view/components/ui/spinner";
import { useToast } from "@/view/components/ui/use-toast";
import { BasicSelect } from "@/view/components/BasicSelect";
import { useAuth } from "@/app/hooks/useAuth";
import { useWhitelabelContext } from "@/app/contexts/whitelabelContext";
import { formatPhoneNumber } from "@/app/utils/formatPhoneNumber";
import { phoneMask } from "@/app/utils/phoneFormater";
import { useContactConfig } from "@/app/hooks/useContactConfig";
import { sendCallStart } from "@/app/services/callStartService/sendCallStart";

const schema = z.object({
  name: z.string().min(1, { message: "Campo obrigatório" }),
  company: z.string().min(1, { message: "Campo obrigatório" }),
  email: z
    .string()
    .min(1, { message: "Campo obrigatório" })
    .email({ message: "E-mail inválido" }),
  phone: z.string().min(14, { message: "WhatsApp inválido" }),
  subject: z.string({ required_error: "Selecione um assunto" }).min(1, {
    message: "Selecione um assunto",
  }),
  message: z.string().trim().min(1, { message: "Campo obrigatório" }),
});

type FormData = z.infer<typeof schema>;

interface CallStartModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export const CallStartModal = ({
  isOpen,
  onOpenChange,
}: CallStartModalProps) => {
  const { user } = useAuth();
  const { appConfig } = useWhitelabelContext();
  const { toast } = useToast();
  const { config } = useContactConfig({ enabled: isOpen });
  const [isEditing, setIsEditing] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const { register, reset, handleSubmit, watch, formState } = form;
  const { errors } = formState;

  useEffect(() => {
    if (!isOpen) return;

    setIsEditing(false);
    reset({
      name: user?.name ?? "",
      company: appConfig?.name ?? "",
      email: user?.email ?? "",
      phone: user?.phone ? formatPhoneNumber(user.phone) : "",
      subject: "",
      message: "",
    });
  }, [isOpen, user, appConfig, reset]);

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: sendCallStart,
    onSuccess: () => {
      onOpenChange(false);
      toast({
        variant: "default",
        title: "Mensagem enviada!",
        description: "Em breve o time da Start entrará em contato.",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Ocorreu um erro. Tente novamente mais tarde",
      });
    },
  });

  const contactHasErrors =
    !!errors.name || !!errors.company || !!errors.email || !!errors.phone;

  useEffect(() => {
    if (contactHasErrors) setIsEditing(true);
  }, [contactHasErrors]);

  const [name, company, email, phone] = watch([
    "name",
    "company",
    "email",
    "phone",
  ]);

  const contactFields = [
    { key: "name", label: "Nome", value: name },
    { key: "company", label: "Empresa", value: company },
    { key: "email", label: "E-mail", value: email },
    { key: "phone", label: "WhatsApp", value: phone },
  ] as const;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent showSupportButton={false} className="max-w-md">
        <DialogHeader className="space-y-1 text-left">
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold text-black01">
            <MessagesSquare className="h-5 w-5" />
            {config.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-black04">
            {config.description}
          </DialogDescription>
        </DialogHeader>

        <FormProvider {...form}>
          <form
            className="flex flex-col"
            onSubmit={handleSubmit((data) => onSubmit(data))}
          >
            <div className="mb-4">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-semibold text-black01">Seus dados</p>
                <button
                  type="button"
                  className="text-sm font-semibold text-primary hover:underline"
                  onClick={() => setIsEditing((prev) => !prev)}
                >
                  {isEditing ? "Concluir" : "Editar"}
                </button>
              </div>

              {isEditing ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {contactFields.map(({ key, label }) => (
                    <Label
                      key={key}
                      htmlFor={key}
                      className="text-xs font-medium text-black04"
                    >
                      {label}
                      <Input
                        id={key}
                        className="mt-1 text-black01"
                        {...register(key)}
                        {...(key === "phone" && {
                          onChange: (e) => {
                            phoneMask(e);
                            register(key).onChange(e);
                          },
                        })}
                      />
                      {errors[key]?.message && (
                        <span className="mt-1 block text-xs font-light text-error">
                          {errors[key]?.message}
                        </span>
                      )}
                    </Label>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-x-4 rounded-xl bg-white02 p-4">
                  {contactFields.map(({ key, label, value }, index) => (
                    <div
                      key={key}
                      className={
                        index < 2 ? "border-b border-gray01 pb-3" : "pt-3"
                      }
                    >
                      <p className="text-xs text-black04">{label}</p>
                      <p
                        className="truncate text-sm font-semibold text-black01"
                        title={value}
                      >
                        {value || "-"}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <BasicSelect
              name="subject"
              label={<span className="text-sm font-medium">Assunto</span>}
              options={config.subjects.map((subject) => ({
                value: subject,
                label: subject,
              }))}
              placeholder="Selecione um assunto"
              error={errors.subject?.message}
              classNameContainer="h-auto"
            />

            <Label htmlFor="message" className="mb-4 text-sm font-medium">
              Mensagem
              <Textarea
                id="message"
                placeholder="Como podemos ajudar?"
                className="mt-2 min-h-24 resize-none text-black01"
                {...register("message")}
              />
              {errors.message?.message && (
                <span className="mt-1 block text-sm font-light text-error">
                  {errors.message.message}
                </span>
              )}
            </Label>

            <Button
              type="submit"
              disabled={isPending}
              className="h-12 w-full bg-primary hover:bg-primary"
            >
              {isPending ? (
                <Spinner className="text-white" size="small" />
              ) : (
                "Enviar"
              )}
            </Button>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
